"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

interface StepActionsValue {
  hasContinueHandler: boolean;
  registerContinueHandler: (fn: () => void) => void;
  unregisterContinueHandler: () => void;
  triggerContinue: () => void;
  formId: string | null;
  registerFormId: (id: string) => void;
  unregisterFormId: () => void;
}

const StepActionsContext = createContext<StepActionsValue | null>(null);

export function StepActionsProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const handlerRef = useRef<(() => void) | null>(null);
  const [hasContinueHandler, setHasContinueHandler] = useState(false);
  const [formId, setFormId] = useState<string | null>(null);

  const registerContinueHandler = useCallback((fn: () => void) => {
    handlerRef.current = fn;
    setHasContinueHandler(true);
  }, []);

  const unregisterContinueHandler = useCallback(() => {
    handlerRef.current = null;
    setHasContinueHandler(false);
  }, []);

  const triggerContinue = useCallback(() => {
    if (!handlerRef.current) return;
    handlerRef.current();
  }, []);

  const registerFormId = useCallback((id: string) => {
    setFormId(id);
  }, []);

  const unregisterFormId = useCallback(() => {
    setFormId(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      hasContinueHandler,
      registerContinueHandler,
      unregisterContinueHandler,
      triggerContinue,
      formId,
      registerFormId,
      unregisterFormId,
    }),
    [
      hasContinueHandler,
      registerContinueHandler,
      unregisterContinueHandler,
      triggerContinue,
      formId,
      registerFormId,
      unregisterFormId,
    ],
  );

  return <StepActionsContext value={contextValue}>{children}</StepActionsContext>;
}

export function useStepActions() {
  const ctx = useContext(StepActionsContext);
  if (!ctx) throw new Error("useStepActions must be used inside StepActionsProvider");
  return ctx;
}
